
import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/components/auth/AuthProvider";
import { Link } from "react-router-dom";

interface ClaimDialogProps {
  centerName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isSubmitting: boolean;
  formData: {
    role: string;
    email: string;
    contactPhone: string;
    verificationNote: string;
  };
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSelectChange: (value: string) => void;
  onSubmit: () => Promise<void>;
  onShowRegistration?: () => void;
}

export function ClaimDialog({ 
  centerName, 
  open, 
  onOpenChange, 
  isSubmitting, 
  formData, 
  onInputChange, 
  onSelectChange,
  onSubmit,
  onShowRegistration
}: ClaimDialogProps) {
  const { user } = useAuth();
  
  // Check if form has required fields filled
  const isFormValid = formData.role && formData.email;
  
  // Handle the claim submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      // Store the claim form data in sessionStorage before showing registration
      sessionStorage.setItem('pendingClaimData', JSON.stringify({
        centerName,
        formData,
        nursingHomeSlug: window.location.pathname.split('/').pop()
      }));
      
      // Close the dialog and show registration form
      onOpenChange(false);
      if (onShowRegistration) {
        onShowRegistration();
      }
      return;
    }
    
    await onSubmit();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Claim {centerName}</DialogTitle>
          <DialogDescription>
            {!user 
              ? "Fill out your details below. You'll need to create an account to complete your claim."
              : "Provide your details to verify your association with this nursing home."
            }
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="role">Your Role at {centerName}</Label>
              <Select 
                name="role" 
                value={formData.role} 
                onValueChange={onSelectChange}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select your role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">Owner</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="administrator">Administrator</SelectItem>
                  <SelectItem value="director">Director of Care</SelectItem>
                  <SelectItem value="staff">Staff Member</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="email">Work Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={onInputChange}
                placeholder="your.name@nursinghome.com"
                required
              />
              <p className="text-xs text-muted-foreground">Preferably use your nursing home email address for faster verification.</p>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="contactPhone">Contact Phone</Label>
              <Input
                id="contactPhone"
                name="contactPhone"
                type="tel"
                value={formData.contactPhone}
                onChange={onInputChange}
                placeholder="Your direct number"
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="verificationNote">Additional Information</Label>
              <Textarea
                id="verificationNote"
                name="verificationNote"
                value={formData.verificationNote}
                onChange={onInputChange}
                placeholder="Please provide any additional details that might help us verify your association with this nursing home."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting || !isFormValid}
            >
              {isSubmitting ? "Submitting..." : !user ? "Create Account & Submit Claim" : "Submit Claim Request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
